import React, { useEffect, useMemo, useState } from "react";
import TextField from "../../components/TextField";
import SubmitButton from "../../components/SubmitButton";
import { Error } from "../../components/Status";
import Select from "../../components/Select";
import { useGetBranches } from "../../hooks/branches/useGet";
import { useGetFormTypes } from "../../hooks/forms/useGet";
import { BRANCH_ROLES } from "../../utils/constant";
import { safeGetUser } from "../../utils/helpers";

const todayYMD = () => new Date().toISOString().slice(0, 10);





const SearchForm = ({ onApply, onClose }) => {
  const today = todayYMD();
  const user = safeGetUser();
  const isPrivileged = !BRANCH_ROLES.includes(user?.roleName);

  const [state, setState] = useState({
    branchId: 0,
    formTypeId: 0,
    startDate: "",
    endDate: "",
    pageNumber: 1,
    pageSize: 20,
  });
  const [error, setError] = useState("");

  const { data: branches, isSuccess: gotBranches } = useGetBranches(1, 100);

  // Filter branches based on privilege
  const filteredBranches = useMemo(() => {
    if (!branches) return [];
    if (isPrivileged) return branches;
    if (!user?.branchCode) return [];
    return branches.filter((b) => String(b.branchCode) === String(user.branchCode));
  }, [branches, isPrivileged, user?.branchCode]);

  // Build options from the filtered list
  const branchOptions = useMemo(
    () =>
      filteredBranches.map((b) => ({
        name: `${b.branchCode} (${b.branchName})`,
        value: b.branchId,
      })),
    [filteredBranches]
  );

  // If user is not privileged, auto-select their branch once data loads
  useEffect(() => {
    if (!isPrivileged && branchOptions.length === 1) {
      const only = branchOptions[0];
      if (only?.value && state.branchId !== only.value) {
        setState((s) => ({ ...s, branchId: only.value }));
      }
    }
  }, [isPrivileged, branchOptions, state.branchId]);

  const { data: formTypes, isSuccess: gotFormTypes } = useGetFormTypes();
  const formTypeOptions = useMemo(
    () =>
      formTypes?.map((t) => ({
        name: t.formTypeName,
        value: t.formTypeId,
      })) ?? [],
    [formTypes]
  );

  const handleChange = ({ target: { name, value } }) =>
    setState((s) => ({ ...s, [name]: value }));

  const handleSubmit = (e) => {
    e.preventDefault();
    setError("");

    if (state.startDate && state.endDate && state.endDate < state.startDate) {
      setError("End Date must be the same as or after Start Date.");
      return;
    }
    if (!state.branchId || Number(state.branchId) <= 0) {
      setError("Please select a Branch.");
      return;
    }

    const payload = {
      BranchId: Number(state.branchId),
      FormTypeId: Number(state.formTypeId) || null,
      StartDate: state.startDate || null,
      EndDate: state.endDate || null,
      PageNumber: Number(state.pageNumber) || 1,
      PageSize: Number(state.pageSize) || 20,
    };

    onApply?.(payload);
    onClose?.();
  };

  return (
    <form className="w-full flex flex-col space-y-5" onSubmit={handleSubmit}>
      {error && <Error>{error}</Error>}

      {gotBranches && (
        <Select
          name="branchId"
   
          onChange={handleChange}
          value={state.branchId}
          placeholder={
            isPrivileged
              ? "Select a branch"
              : "Your branch (auto-selected)"
          }
          label="Branch"
          options={branchOptions}
          required
        />
      )}

      {gotFormTypes && (
        <Select
          name="formTypeId"
          onChange={handleChange}
          placeholder="All types"
          label="Form Type"
          options={[{ name: "All", value: 0 }, ...formTypeOptions]}
        />
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <TextField
          label="Start Date (optional)"
          type="date"
          name="startDate"
          value={state.startDate}
          onChange={handleChange}
          min="2000-01-01"
          max={today}
        />
        <TextField
          label="End Date (optional)"
          type="date"
          name="endDate"
          value={state.endDate}
          onChange={handleChange}
          min={state.startDate || "2000-01-01"}
          max={today}
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <TextField
          label="Page"
          type="number"
          name="pageNumber"
          value={state.pageNumber}
          onChange={handleChange}
          min="1"
          step="1"
          required
        />
        <TextField
          label="Page Size"
          type="number"
          name="pageSize"
          value={state.pageSize}
          onChange={handleChange}
          min="1"
          step="1"
          required
        />
      </div>

      <SubmitButton>Apply Filters</SubmitButton>
    </form>
  );
};

export default SearchForm;
